import React from 'react';

import { defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { aiMessages } from '@atlaskit/editor-common/ai-messages/ai';
import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages/ai-config-items';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import ChangeToneIcon from '@atlaskit/icon/core/microphone';
import PaintPaletteIcon from '@atlaskit/icon/core/paint-palette';

import type {
	ChangeToneBusinessCasualIntentSchema,
	ChangeToneEducationalIntentSchema,
	ChangeToneEmpatheticIntentSchema,
	ChangeToneNeutralIntentSchema,
	ChangeToneProfessionalIntentSchema,
} from '../../../../../types/prompt-request-types';
import { ToneCasualIcon } from '../../../../icons/prompt/toneCasual';
import { ToneEducationalIcon } from '../../../../icons/prompt/toneEducational';
import { ToneEmpatheticIcon } from '../../../../icons/prompt/toneEmpathetic';
import { ToneNeutralIcon } from '../../../../icons/prompt/toneNeutral';
import { ToneProfessionalIcon } from '../../../../icons/prompt/toneProfessional';
import type { ConvoAiChangeToneFeatureIds } from '../../../../utils/request/types';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemFactoryOptions,
	EditorPluginAIConfigItemWithOptionsFactory,
	EditorPluginAIConfigItem,
	EditorPluginAIConfigItemFactory,
} from '../../config-items';

const writingStyleMessages = defineMessages({
	rovoDefaultTitle: {
		id: 'editor-plugin-ai.prebuilt.config-items.prompts.change-tone.change-tone.rovoDefaultTitle',
		defaultMessage: 'Change tone to Rovo default',
		description: 'Title for changing selected editor content to the user’s default Rovo style',
	},
	rovoDefaultShortTitle: {
		id: 'editor-plugin-ai.prebuilt.config-items.prompts.change-tone.change-tone.rovoDefaultShortTitle',
		defaultMessage: 'Rovo default',
		description: 'Short label for the user’s default Rovo writing style',
	},
	rovoDefaultDescription: {
		id: 'editor-plugin-ai.prebuilt.config-items.prompts.change-tone.change-tone.rovoDefaultDescription',
		defaultMessage: 'Changes the content to your default Rovo writing style',
		description: 'Description for changing selected editor content to the default Rovo style',
	},
	creativeTitle: {
		id: 'editor-plugin-ai.prebuilt.config-items.prompts.change-tone.change-tone.creativeTitle',
		defaultMessage: 'Change tone to creative',
		description: 'Title for changing selected editor content to a creative writing style',
	},
	creativeShortTitle: {
		id: 'editor-plugin-ai.prebuilt.config-items.prompts.change-tone.change-tone.creativeShortTitle',
		defaultMessage: 'Creative',
		description: 'Short label for the creative writing style',
	},
	creativeDescription: {
		id: 'editor-plugin-ai.prebuilt.config-items.prompts.change-tone.change-tone.creativeDescription',
		defaultMessage: 'Changes the content to a creative writing style',
		description: 'Description for changing selected editor content to a creative style',
	},
});

function createChangeToneOption({
	key,
	title,
	description,
	intentSchemaId,
	featureId,
	icon,
	shortTitle,
	adfPrompt,
	factoryOptions,
}: Pick<EditorPluginAIConfigItem, 'key' | 'title' | 'description' | 'icon'> & {
	adfPrompt: boolean;
	factoryOptions?: EditorPluginAIConfigItemFactoryOptions;
	featureId: ConvoAiChangeToneFeatureIds;
	intentSchemaId:
		| 'DISABLED'
		| ChangeToneBusinessCasualIntentSchema
		| ChangeToneEducationalIntentSchema
		| ChangeToneEmpatheticIntentSchema
		| ChangeToneNeutralIntentSchema
		| ChangeToneProfessionalIntentSchema;
	shortTitle: MessageDescriptor;
}): EditorPluginAIConfigItem {
	return {
		key,
		title,
		description,
		icon,
		showInRefineDropdown: true,
		intentSchemaId,
		featureId,
		adfPrompt,
		requiresTextualContent: adfPrompt,
		nestingConfig: {
			id: 'change-tone-to',
			parentTestId: 'change-tone-to',
			parentTitle: aiMessages.changeToneToolbarDropdownIconTitle,
			shortTitle,
			icon: () => <ChangeToneIcon label="" spacing="spacious" />,
		},
		actions: {
			empty: [
				createInsertBelow({ appearance: 'secondary', factoryOptions }),
				createReplace({ appearance: 'primary' }),
			],
			selection: [
				createInsertBelow({ appearance: 'secondary', factoryOptions }),
				createReplace({ appearance: 'primary' }),
			],
		},
		getBackendModel: () => {
			return 'assistance-service';
		},
	};
}

export const changeToneToRovoDefault: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = 'change_tone_rovo_default';

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_ROVO_DEFAULT,
		title: writingStyleMessages.rovoDefaultTitle,
		shortTitle: writingStyleMessages.rovoDefaultShortTitle,
		description: writingStyleMessages.rovoDefaultDescription,
		intentSchemaId: 'DISABLED',
		featureId,
		adfPrompt: true,
		icon: () => <RovoIcon label="" />,
		factoryOptions: options,
	});
};

export const changeToneToRovoDefaultWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToRovoDefault(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToCreative: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = 'change_tone_creative';

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_CREATIVE,
		title: writingStyleMessages.creativeTitle,
		shortTitle: writingStyleMessages.creativeShortTitle,
		description: writingStyleMessages.creativeDescription,
		intentSchemaId: 'DISABLED',
		featureId,
		adfPrompt: true,
		icon: () => <PaintPaletteIcon label="" />,
		factoryOptions: options,
	});
};

export const changeToneToCreativeWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToCreative(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToNeutral: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = 'change_tone_neutral';

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_NEUTRAL,
		title: aiConfigItemMessages.changeToneNeutralToneTitle,
		shortTitle: aiConfigItemMessages.changeToneNeutralToneShortTitle,
		description: aiConfigItemMessages.changeToneNeutralToneDescription,
		intentSchemaId: 'change_tone_neutral_intent_schema.json',
		featureId,
		adfPrompt: false,
		icon: () => <ToneNeutralIcon />,
		factoryOptions: options,
	});
};

export const changeToneToNeutralWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToNeutral(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToProfessional: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = 'change_tone_professional_adf_gpt41mini';

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_PROFESSIONAL,
		title: aiMessages.moreProfessionalToneToolbarDropdownIconTitle,
		shortTitle: aiConfigItemMessages.changeToneProfessionalToneShortTitle,
		description: aiConfigItemMessages.changeToneProfessionalToneDescription,
		intentSchemaId: 'change_tone_professional_intent_schema.json',
		featureId,
		adfPrompt: true,
		icon: () => <ToneProfessionalIcon />,
		factoryOptions: options,
	});
};

export const changeToneToProfessionalWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToProfessional(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToCasual: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = 'change_tone_casual_adf_gpt41mini';

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_CASUAL,
		title: aiMessages.moreCasualToneToolbarDropdownIconTitle,
		shortTitle: aiConfigItemMessages.changeToneCasualToneShortTitle,
		description: aiConfigItemMessages.changeToneCasualToneDescription,
		intentSchemaId: 'change_tone_business_casual_intent_schema.json',
		featureId,
		adfPrompt: true,
		icon: () => <ToneCasualIcon />,
		factoryOptions: options,
	});
};

export const changeToneToCasualWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToCasual(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToEducational: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = 'change_tone_educational_adf_gpt41mini';

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EDUCATIONAL,
		title: aiConfigItemMessages.changeToneEducationalToneTitle,
		shortTitle: aiConfigItemMessages.changeToneEducationalToneShortTitle,
		description: aiConfigItemMessages.changeToneEducationalToneDescription,
		intentSchemaId: 'change_tone_educational_intent_schema.json',
		featureId,
		adfPrompt: true,
		icon: () => <ToneEducationalIcon />,
		factoryOptions: options,
	});
};

export const changeToneToEducationalWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToEducational(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToEmpathetic: EditorPluginAIConfigItemFactory = (options) => {
	const featureId: ConvoAiChangeToneFeatureIds = 'change_tone_empathetic_adf_gpt41mini';

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EMPATHETIC,
		title: aiMessages.moreEmpatheticToneToolbarDropdownIconTitle,
		shortTitle: aiConfigItemMessages.changeToneEmpatheticToneShortTitle,
		description: aiConfigItemMessages.changeToneEmpatheticToneDescription,
		intentSchemaId: 'change_tone_empathetic_intent_schema.json',
		featureId,
		adfPrompt: true,
		icon: () => <ToneEmpatheticIcon />,
		factoryOptions: options,
	});
};

export const changeToneToEmpatheticWithOptions: EditorPluginAIConfigItemWithOptionsFactory = (
	options,
) => ({
	config: changeToneToEmpathetic(options),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});
